import {
	ATTENTION_STEP_ICON_MARKUP,
	CHEVRON_ICON_MARKUP,
	COMPLETED_STEP_ICON_MARKUP,
	STEP_ATTENTION_CLASS,
	STEP_BUTTON_CLASS,
	STEP_BUTTON_ICON_ATTENTION_CLASS,
	STEP_BUTTON_ICON_CLASS,
	STEP_BUTTON_ICON_COMPLETE_CLASS,
	STEP_BUTTON_ICON_HIDDEN_CLASS,
	STEP_BUTTON_LABEL_CLASS,
	STEP_BUTTON_SELECTOR,
	STEP_CHILDREN_CLASS,
	STEP_CLICKABLE_CLASS,
	STEP_CURRENT_CLASS,
	STEP_DONE_CLASS,
	STEP_GROUP_CLASS,
	STEP_HAS_CHILDREN_CLASS,
	STEP_ICON_TYPE_DATA_ATTRIBUTE,
	STEP_ITEM_CLASS,
	STEP_LOCKED_CLASS,
	STEP_NOT_DONE_CLASS,
	STEP_OPEN_CLASS,
	STEP_ROW_CLASS,
	STEP_SUBSTEP_CLASS,
	STEP_TOGGLE_CLASS,
} from './sidebar-constants.js';
import { toPositiveInteger } from './sidebar-utils.js';

/**
* Responsibility:
* - Apply the existing sidebar state classes from one explicit resolved step model.
*
* Touches:
* - Writes `.sd-configurator__step*` state classes onto one list item.
*
* Guarantees:
* - Produces the same top-level class contract expected by existing configurator styling.
*/
function applyResolvedStepStateClasses(item, stepState) {
	if (!(item instanceof HTMLElement)) {
		return;
	}

	item.classList.add(STEP_ITEM_CLASS);
	item.classList.toggle(STEP_CURRENT_CLASS, stepState?.current === true);
	item.classList.toggle(STEP_DONE_CLASS, stepState?.complete === true);
	item.classList.toggle(STEP_ATTENTION_CLASS, stepState?.attention === true);
	item.classList.toggle(
		STEP_NOT_DONE_CLASS,
		stepState?.complete !== true && stepState?.attention !== true && stepState?.current !== true
	);
	item.classList.toggle(STEP_CLICKABLE_CLASS, stepState?.clickable === true);
	item.classList.toggle(STEP_LOCKED_CLASS, stepState?.clickable !== true && stepState?.current !== true);
}

/**
* Responsibility:
* - Append the shared label and stable status-slot markup to one sidebar navigation button.
*
* Touches:
* - Creates the label span consumed by theme styling and appends one stable status span.
*
* Guarantees:
* - Produces one stable label/icon DOM contract owned directly by the plugin renderer.
*/
function populateNavigationButton(button, label, iconType = '') {
	if (!(button instanceof HTMLButtonElement)) {
		return;
	}

	const labelElement = document.createElement('span');
	labelElement.className = STEP_BUTTON_LABEL_CLASS;
	labelElement.textContent = String(label || '').trim();
	button.appendChild(labelElement);

	const iconElement = document.createElement('span');
	const normalizedIconType = String(iconType || '').trim();
	iconElement.className = STEP_BUTTON_ICON_CLASS;
	iconElement.setAttribute('aria-hidden', 'true');
	iconElement.classList.add(
		normalizedIconType === 'attention'
			? STEP_BUTTON_ICON_ATTENTION_CLASS
			: normalizedIconType === 'complete'
				? STEP_BUTTON_ICON_COMPLETE_CLASS
				: STEP_BUTTON_ICON_HIDDEN_CLASS
	);
	if (normalizedIconType !== '') {
		iconElement.dataset[STEP_ICON_TYPE_DATA_ATTRIBUTE] = normalizedIconType;
	}
	iconElement.innerHTML = normalizedIconType === 'attention'
		? ATTENTION_STEP_ICON_MARKUP
		: normalizedIconType === 'complete'
			? COMPLETED_STEP_ICON_MARKUP
			: '';
	button.appendChild(iconElement);
}

/**
* Responsibility:
* - Create one sidebar navigation button for a primary step or substep target.
*
* Touches:
* - Creates the button element and applies current/disabled accessibility attributes.
*
* Guarantees:
* - Returns a `.sd-configurator__step-button` button whose `value` is the target GF page number.
*/
function createNavigationButton(stepState) {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = STEP_BUTTON_CLASS;
	button.value = String(toPositiveInteger(stepState?.targetPage));
	populateNavigationButton(
		button,
		String(stepState?.label || '').trim() || `Step ${toPositiveInteger(stepState?.targetPage)}`,
		String(stepState?.iconType || '').trim()
	);

	if (String(stepState?.currentAriaValue || '').trim() !== '') {
		button.setAttribute('aria-current', String(stepState.currentAriaValue).trim());
	}

	if (stepState?.disabled === true) {
		button.disabled = true;
		button.setAttribute('aria-disabled', 'true');
	}

	return button;
}

/**
* Responsibility:
* - Create the grouped-step chevron toggle button using the existing site chevron icon.
*
* Touches:
* - Creates one icon-only button with `aria-expanded` and `aria-controls`.
*
* Guarantees:
* - Returns a stable `.sd-configurator__step-toggle` button that can drive the accordion behavior.
*/
function createPrimaryToggleButton(stepState) {
	const toggleButton = document.createElement('button');
	toggleButton.type = 'button';
	toggleButton.className = STEP_TOGGLE_CLASS;
	toggleButton.setAttribute('aria-expanded', stepState?.expanded === true ? 'true' : 'false');
	toggleButton.setAttribute('aria-controls', String(stepState?.panelId || '').trim());
	toggleButton.setAttribute('aria-label', `Toggle ${String(stepState?.label || '').trim() || 'step details'}`);
	toggleButton.innerHTML = CHEVRON_ICON_MARKUP;
	return toggleButton;
}

/**
* Responsibility:
* - Render one nested substep item from one explicit resolved step model.
*
* Touches:
* - Reuses the existing sidebar step classes for one concrete GF page number.
*
* Guarantees:
* - Returns a rendered substep `<li>` with no additional state inference.
*/
function renderSubstepItem(stepState) {
	const targetPage = toPositiveInteger(stepState?.targetPage);
	if (targetPage <= 0) {
		return null;
	}

	const item = document.createElement('li');
	item.classList.add(STEP_SUBSTEP_CLASS);
	applyResolvedStepStateClasses(item, stepState);
	item.appendChild(createNavigationButton(stepState));

	return item;
}

/**
* Responsibility:
* - Render one grouped primary-step item from one explicit resolved step model.
*
* Touches:
* - Renders the primary row, optional chevron toggle, and nested substep list.
*
* Guarantees:
* - Produces grouped markup without re-inferring business state from the DOM.
*/
function renderPrimaryStepItem(stepState) {
	const targetPage = toPositiveInteger(stepState?.targetPage);
	if (targetPage <= 0) {
		return null;
	}

	const item = document.createElement('li');
	item.classList.add(STEP_GROUP_CLASS);
	if (stepState?.hasSubsteps === true) {
		item.classList.add(STEP_HAS_CHILDREN_CLASS);
	}
	if (stepState?.expanded === true) {
		item.classList.add(STEP_OPEN_CLASS);
	}
	applyResolvedStepStateClasses(item, stepState);

	const row = document.createElement('div');
	row.className = STEP_ROW_CLASS;
	row.appendChild(createNavigationButton(stepState));

	if (stepState?.hasSubsteps === true) {
		row.appendChild(createPrimaryToggleButton(stepState));

		const panel = document.createElement('ol');
		panel.id = String(stepState?.panelId || '').trim();
		panel.className = STEP_CHILDREN_CLASS;
		panel.hidden = stepState?.expanded !== true;

		(Array.isArray(stepState?.substeps) ? stepState.substeps : []).forEach((substepState) => {
			const substepItem = renderSubstepItem(substepState);
			if (substepItem instanceof HTMLElement) {
				panel.appendChild(substepItem);
			}
		});

		item.appendChild(row);
		item.appendChild(panel);
		return item;
	}

	item.appendChild(row);
	return item;
}

/**
* Responsibility:
* - Serialize one resolved step state into a stable signature payload for render-skip checks.
*
* Touches:
* - Reads only the renderer-relevant fields from one resolved primary or substep state object.
*
* Guarantees:
* - Returns a minimal plain-object signature that changes only when rendered sidebar markup should change.
*/
function serializeStepRenderSignature(stepState) {
	return {
		targetPage: toPositiveInteger(stepState?.targetPage),
		label: String(stepState?.label || '').trim(),
		current: stepState?.current === true,
		complete: stepState?.complete === true,
		attention: stepState?.attention === true,
		clickable: stepState?.clickable === true,
		disabled: stepState?.disabled === true,
		currentAriaValue: String(stepState?.currentAriaValue || '').trim(),
		iconType: String(stepState?.iconType || '').trim(),
		hasSubsteps: stepState?.hasSubsteps === true,
		expanded: stepState?.expanded === true,
		substeps: (Array.isArray(stepState?.substeps) ? stepState.substeps : []).map(serializeStepRenderSignature),
	};
}

/**
* Responsibility:
* - Build the stable sidebar render signature for one resolved sidebar view model.
*
* Touches:
* - Reads the resolved grouped primary-step state only.
*
* Guarantees:
* - Returns the same signature string whenever the rendered sidebar markup would be identical.
*/
export function buildStepsRenderSignature(viewModel) {
	return JSON.stringify(
		(Array.isArray(viewModel?.primaryStepStates) ? viewModel.primaryStepStates : []).map(serializeStepRenderSignature)
	);
}

/**
* Responsibility:
* - Render the grouped sidebar step list from the resolved sidebar view model.
*
* Touches:
* - Replaces the contents of the shared `.sd-configurator__steps` element.
*
* Guarantees:
* - Keeps the existing top-level step classes intact while rendering grouped primary/substep markup.
*/
export function renderStepsList(listElement, viewModel) {
	if (!(listElement instanceof HTMLElement)) {
		return;
	}

	const fragment = document.createDocumentFragment();
	(Array.isArray(viewModel?.primaryStepStates) ? viewModel.primaryStepStates : []).forEach((stepState) => {
		const stepItem = renderPrimaryStepItem(stepState);
		if (stepItem instanceof HTMLElement) {
			fragment.appendChild(stepItem);
		}
	});

	listElement.innerHTML = '';
	listElement.appendChild(fragment);
}

/**
* Responsibility:
* - Resolve the direct nested substep panel element for one rendered grouped primary item.
*
* Touches:
* - Reads the immediate child nodes of the grouped item only.
*
* Guarantees:
* - Returns the current accordion panel or `null` when the item has no nested substeps.
*/
export function resolvePrimaryStepPanel(item) {
	if (!(item instanceof HTMLElement)) {
		return null;
	}

	return Array.from(item.children).find((child) => {
		return child instanceof HTMLElement && child.classList.contains(STEP_CHILDREN_CLASS);
	}) || null;
}

/**
* Responsibility:
* - Resolve the primary-step navigation button for one rendered grouped item.
*
* Touches:
* - Reads the direct row wrapper and the contained `.sd-configurator__step-button`.
*
* Guarantees:
* - Returns the primary step button or `null` when the item is malformed.
*/
export function resolvePrimaryStepButton(item) {
	if (!(item instanceof HTMLElement)) {
		return null;
	}

	const row = Array.from(item.children).find((child) => {
		return child instanceof HTMLElement && child.classList.contains(STEP_ROW_CLASS);
	});
	if (!(row instanceof HTMLElement)) {
		return null;
	}

	const button = row.querySelector(STEP_BUTTON_SELECTOR);
	return button instanceof HTMLButtonElement ? button : null;
}

/**
* Responsibility:
* - Resolve the chevron toggle button for one rendered grouped item.
*
* Touches:
* - Reads the direct row wrapper and the contained `.sd-configurator__step-toggle`.
*
* Guarantees:
* - Returns the chevron toggle button or `null` when the item is not expandable.
*/
export function resolvePrimaryToggleButton(item) {
	if (!(item instanceof HTMLElement)) {
		return null;
	}

	const row = Array.from(item.children).find((child) => {
		return child instanceof HTMLElement && child.classList.contains(STEP_ROW_CLASS);
	});
	if (!(row instanceof HTMLElement)) {
		return null;
	}

	const toggleButton = row.querySelector(`.${STEP_TOGGLE_CLASS}`);
	return toggleButton instanceof HTMLButtonElement ? toggleButton : null;
}

/**
* Responsibility:
* - Return the primary target page represented by one rendered grouped item.
*
* Touches:
* - Reads the primary navigation button `value`.
*
* Guarantees:
* - Returns a positive target page or `0` when the current item cannot be navigated.
*/
export function resolveRenderedPrimaryTargetPage(item) {
	const button = resolvePrimaryStepButton(item);
	return button instanceof HTMLButtonElement ? toPositiveInteger(button.value) : 0;
}

/**
* Responsibility:
* - Resolve the current primary-step target from the already-rendered sidebar DOM.
*
* Touches:
* - Reads rendered grouped primary-step items and their current-state classes.
*
* Guarantees:
* - Returns the current expandable primary-step target or `0` when none is active.
*/
export function resolveRenderedCurrentPrimaryTargetPage(listElement) {
	if (!(listElement instanceof HTMLElement)) {
		return 0;
	}

	const currentPrimaryItem = Array.from(listElement.children).find((child) => {
		return child instanceof HTMLElement
			&& child.classList.contains(STEP_GROUP_CLASS)
			&& child.classList.contains(STEP_CURRENT_CLASS)
			&& resolvePrimaryStepPanel(child) instanceof HTMLElement;
	});

	return currentPrimaryItem instanceof HTMLElement
		? resolveRenderedPrimaryTargetPage(currentPrimaryItem)
		: 0;
}
